import { useEffect, useMemo, useRef, useState } from 'react'
import { usePageActions, usePageTitle } from '../AppLayout'
import { api } from '../api/client'
import type { CaseDetail, ResearchQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { useFaelle } from '../hooks/useFaelle'
import { nachDatum, nachTitel, reiheAus } from '../hooks/faelleRegeln'
import { alsTag, neuSeit } from '../hooks/uebersichtRegeln'
import { useListParams } from '../hooks/useListParams'
import { Button } from '../components/common/Button'
import { Input, Select, Toggle } from '../components/common/Controls'
import { EmptyState, ErrorBox, Loading } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed, FilterGrow }
  from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { Umschalter } from '../components/common/Umschalter'
import { FaelleKennzahlen } from '../components/faelle/FaelleKennzahlen'
import { FaelleListe } from '../components/faelle/FaelleListe'
import { FallDetail } from '../components/faelle/FallDetail'
import { MasterDetail } from '../components/layout/MasterDetail'
import { options } from './researchVocabulary'
import s from './ResearchPage.module.scss'

const PERMISSION = 'research:read'

type Sicht = 'datum' | 'titel'

const SICHTEN: { wert: Sicht; text: string }[] = [
  { wert: 'datum', text: 'Nach Datum' },
  { wert: 'titel', text: 'Nach Titel' },
]

/**
 * Die Fälle als Master–Detail.
 *
 * LINKS die Bewegungsliste — nach Datum jede Entwicklung eine Zeile, Neues
 * oben, Termine darüber; nach Titel jeder Fall unter seinem Titel —, RECHTS
 * der gewählte Fall mit Kennzahlen, Sachstand, Verlauf und Belegen. Die
 * Filter laufen über die Fallliste am Server und gelten in beiden Sichten;
 * sie stehen in der Adresse, Sicht und Auswahl nicht.
 *
 * Das Detail kommt je Fall EINMAL vom Server und bleibt für die Sitzung im
 * Speicher: Wer zwischen zwei Fällen hin- und herblättert, wartet nicht
 * zweimal.
 */
export function ResearchPage() {
  const allowed = useHasPermission(PERMISSION)
  const [sicht, setSicht] = useState<Sicht>('datum')
  const [gewaehlt, setGewaehlt] = useState<number | null>(null)

  const list = useListParams({
    // `isin` steht nicht in der Leiste: Die Instrumentseite verlinkt damit
    // auf „alle Fälle zu diesem Titel".
    filters: { q: '', case_type: '', status: '', min_impact: '', active_only: '', isin: '' },
    sort: 'last_material_update_at',
    descending: true,
  })

  const params: ResearchQuery = {
    q: list.filters.q.trim() || undefined,
    case_type: list.filters.case_type || undefined,
    status: list.filters.status || undefined,
    min_impact: list.filters.min_impact ? Number(list.filters.min_impact) : undefined,
    active_only: list.filters.active_only === '1' || undefined,
    isin: list.filters.isin || undefined,
    sort: 'last_material_update_at',
    dir: 'desc',
  }

  const { daten, laedt, fehler, denied } = useFaelle(params, allowed)

  // Heute und „seit gestern 18:00" stehen fest, solange die Seite offen ist.
  const [heute] = useState(() => alsTag(new Date()))
  const [seit] = useState(() => neuSeit(new Date()))

  const gruppen = useMemo(() => {
    if (!daten) return []
    return sicht === 'datum'
      ? nachDatum(daten.faelle, daten.bewegungen, daten.termine, heute, seit)
      : nachTitel(daten.faelle)
  }, [daten, sicht, heute, seit])
  const reihe = useMemo(() => reiheAus(gruppen), [gruppen])

  // Ohne Auswahl der erste Fall der Liste; verschwindet der gewählte durch
  // einen Filterwechsel, rückt wieder der erste nach.
  const offenId = gewaehlt !== null && reihe.includes(gewaehlt) ? gewaehlt : reihe[0] ?? null
  const index = offenId === null ? -1 : reihe.indexOf(offenId)

  function springe(schritt: number) {
    if (reihe.length === 0) return
    setGewaehlt(reihe[(index + schritt + reihe.length) % reihe.length])
  }

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const ziel = event.target as HTMLElement | null
      if (ziel && /INPUT|TEXTAREA|SELECT/.test(ziel.tagName)) return
      if (event.key === 'ArrowDown') { event.preventDefault(); springe(1) }
      if (event.key === 'ArrowUp') { event.preventDefault(); springe(-1) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // Das Detail des gewählten Falls, mit Gedächtnis je Fall.
  const [detail, setDetail] = useState<CaseDetail | null>(null)
  const [detailLaedt, setDetailLaedt] = useState(false)
  const gemerkt = useRef(new Map<number, CaseDetail>())
  useEffect(() => {
    if (offenId === null) { setDetail(null); return }
    const bekannt = gemerkt.current.get(offenId)
    if (bekannt) { setDetail(bekannt); return }
    setDetail(null)
    setDetailLaedt(true)
    const controller = new AbortController()
    api.researchCase(offenId, {}, controller.signal).then((fall) => {
      gemerkt.current.set(offenId, fall)
      if (!controller.signal.aborted) setDetail(fall)
    }).catch(() => { /* Die Liste steht; das Detail bleibt leer. */ })
      .finally(() => { if (!controller.signal.aborted) setDetailLaedt(false) })
    return () => controller.abort()
  }, [offenId])

  const zeilenAnzahl = gruppen.reduce((n, g) => n + g.zeilen.length, 0)
  usePageTitle('Recherche', daten
    ? `${daten.faelle.length} von ${daten.gesamt ?? daten.faelle.length} Vorgängen · laufende Vorgänge zu Titeln, ändert keine Bewertung`
    : undefined)
  usePageActions(useMemo(() => (
    <Umschalter optionen={SICHTEN} wert={sicht} onChange={setSicht} label="Ordnung der Liste" />
  ), [sicht]))

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION} subject="die Recherche" />
  }

  const position = (() => {
    if (offenId === null) return ''
    const gruppe = gruppen.find((g) => g.zeilen.some((z) => z.fallId === offenId))
    if (!gruppe) return `${index + 1} von ${reihe.length}`
    const i = gruppe.zeilen.findIndex((z) => z.fallId === offenId) + 1
    return `${gruppe.label} · ${i} von ${gruppe.zeilen.length}`
  })()

  return (
    <>
      <FaelleKennzahlen offen={daten?.zahlen?.open_cases ?? null}
                        hoch={daten?.zahlen?.high_impact ?? null}
                        faellig={daten?.zahlen?.due_actions ?? null}
                        bewegt={daten?.bewegt7 ?? null} />

      <FilterBar>
        <FilterGrow>
          <Input label="Suche" placeholder="Vorgang, Aktenzeichen, Titel oder ISIN"
                 // OHNE autoFocus: Die Pfeiltasten sollen die Auswahl bewegen.
                 value={list.filters.q}
                 onChange={(event) => list.setFilter('q', event.target.value)} />
        </FilterGrow>
        <FilterFixed>
          <Select label="Art" value={list.filters.case_type}
                  onChange={(event) => list.setFilter('case_type', event.target.value)}>
            <option value="">alle</option>
            {options('case_type').map(([wert, name]) => (
              <option key={wert} value={wert}>{name}</option>
            ))}
          </Select>
        </FilterFixed>
        <FilterFixed>
          <Select label="Stand" value={list.filters.status}
                  onChange={(event) => list.setFilter('status', event.target.value)}>
            <option value="">alle</option>
            {options('case_status').map(([wert, name]) => (
              <option key={wert} value={wert}>{name}</option>
            ))}
          </Select>
        </FilterFixed>
        <FilterFixed>
          <Select label="Wirkung ab" value={list.filters.min_impact}
                  onChange={(event) => list.setFilter('min_impact', event.target.value)}>
            <option value="">alle</option>
            <option value="1">1 — gering</option>
            <option value="2">2 — Schwankung möglich</option>
            <option value="3">3 — Neubewertung möglich</option>
            <option value="4">4 — Korridor unzuverlässig</option>
          </Select>
        </FilterFixed>
        <FilterFixed>
          <Toggle on={list.filters.active_only === '1'}
                  onChange={(on) => list.setFilter('active_only', on ? '1' : '')}>
            nur laufende
          </Toggle>
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>Zurücksetzen</Button>
        </FilterEnd>
      </FilterBar>

      {fehler && <ErrorBox>{fehler}</ErrorBox>}

      <MasterDetail breite="minmax(340px, 38%)"
        liste={laedt && !daten ? (
          <Loading>lädt Fälle …</Loading>
        ) : (
          <FaelleListe gruppen={gruppen} gewaehlt={offenId} onWaehle={setGewaehlt}
                       leer={list.touched ? 'Kein Vorgang passt zu diesen Filtern.' : 'Es sind noch keine Fälle erfasst.'}
                       fuss={`${zeilenAnzahl} ${sicht === 'datum' ? 'Bewegungen und Termine' : 'Vorgänge'} · ${gruppen.length} ${sicht === 'datum' ? 'Tage' : 'Titel'}`} />
        )}
        detail={offenId !== null ? (
          <FallDetail fall={detail} laedt={detailLaedt} position={position}
                      onVorherige={() => springe(-1)} onNaechste={() => springe(1)} />
        ) : !laedt && (
          <div className={s.ohne}>
            <EmptyState title="kein Fall gewählt">
              Links einen Vorgang wählen — oder die Filter lockern.
            </EmptyState>
          </div>
        )} />
    </>
  )
}
